import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { filesService, resolveUploadUrl, type UploadFolder } from "@/services/filesService";
import { getApiErrorMessage } from "@/services/httpClient";
import { cn } from "@/lib/utils";
import { type ImageStandardKey, specForFolder } from "@/lib/imageStandards";
import { processImageForUpload } from "@/lib/processImageForUpload";

export interface ImageUploadProps {
  label?: string;
  value?: string;
  onChange: (url: string) => void;
  folder: UploadFolder;
  variant?: "logo" | "banner";
  disabled?: boolean;
  className?: string;
  hint?: string;
  /** stacked = preview above controls (better in side sheets) */
  layout?: "inline" | "stacked";
}

const ImageUpload = ({
  label = "Image",
  value,
  onChange,
  folder,
  variant,
  disabled,
  className,
  hint,
  layout = "inline",
}: ImageUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const spec = specForFolder(folder, variant);
  const standardKey: ImageStandardKey = spec.key;
  const isBanner = standardKey === "storeBanner";

  const handleFile = async (file: File | undefined) => {
    if (!file || disabled) return;
    setError(null);
    setUploading(true);
    try {
      const processed = await processImageForUpload(file, spec);
      const result = await filesService.upload(processed, folder);
      onChange(result.fileUrl);
    } catch (err) {
      setError(getApiErrorMessage(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const previewUrl = value ? resolveUploadUrl(value) : undefined;
  const defaultHint = `${spec.label} · ${spec.hint}`;

  const previewClass = standardKey === "storeBanner"
    ? "w-full max-w-lg aspect-[3/1]"
    : standardKey === "homeBanner"
    ? "h-48 w-48"
    : "h-32 w-32";

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label>{label}</Label>}
      <div className={cn("flex gap-3", layout === "stacked" ? "flex-col" : "flex-col sm:flex-row")}>
        <div
          className={cn(
            "relative flex shrink-0 items-center justify-center overflow-hidden rounded-xl border border-dashed border-border bg-white",
            !previewUrl && "cursor-pointer hover:bg-muted/30",
            previewClass,
          )}
          onClick={() => !previewUrl && !disabled && !uploading && inputRef.current?.click()}
        >
          {previewUrl ? (
            <>
              <img src={previewUrl} alt="" className="h-full w-full object-contain" />
              {!disabled && (
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  className="absolute right-1 top-1 h-7 w-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange("");
                  }}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          ) : uploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : (
            <ImagePlus className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-col gap-2 min-w-0">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            disabled={disabled || uploading}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? "Processing…" : previewUrl ? "Replace image" : "Upload image"}
          </Button>
          <p className="text-xs text-muted-foreground">{hint ?? defaultHint}</p>
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
